import {
  applyAIChatEvent,
  type AIChatBlock,
  type AIChatConversation,
  type AIChatEvent,
  type AIChatMessage,
} from '@recipes/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/auth-provider';
import { describeError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { useSse } from '@/lib/sse/use-sse';

function resolveStreamUrl(streamUrl: string): string {
  return /^https?:\/\//.test(streamUrl) ? streamUrl : api.http.url(streamUrl);
}

/** The chat's availability for this account; polled rarely, refreshed when the panel opens. */
export function useAiChatStatus(enabled = true) {
  const { status } = useAuth();
  return useQuery({
    queryKey: queryKeys.aiChat.status,
    queryFn: () => api.aiChat.status(),
    enabled: enabled && status === 'authenticated',
    staleTime: 60_000,
    retry: false,
  });
}

export interface LiveTurn {
  /** The assistant message being written. */
  messageId: string;
  blocks: AIChatBlock[];
  streamUrl: string;
}

/**
 * One conversation in the drawer: its messages, the turn that is running (built from the
 * stream, the same way the server stores it) and the actions of the composer.
 */
export function useAiChatConversation(
  onFinished?: (blocks: AIChatBlock[]) => void,
  /** Every event of a turn this browser follows – the voice reads the text as it arrives. */
  onLiveEvent?: (event: AIChatEvent) => void,
) {
  const queryClient = useQueryClient();
  const [conversation, setConversation] = useState<AIChatConversation | null>(null);
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [live, setLive] = useState<LiveTurn | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The container's error code of the last turn (translated by the panel), if any. */
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const liveRef = useRef<LiveTurn | null>(null);
  liveRef.current = live;
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;
  const liveEventRef = useRef(onLiveEvent);
  liveEventRef.current = onLiveEvent;
  /** The stream delivered `done` or `error`: a closed connection is then no loss. */
  const terminalRef = useRef(false);

  const invalidateLists = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['ai-chat', 'conversations'] }),
    [queryClient],
  );

  const load = useCallback(async (id: string) => {
    const detail = await api.aiChat.conversation(id);
    setConversation(detail.conversation);
    setMessages(detail.messages.filter((m) => m.status !== 'running'));
    const running = detail.messages.find((m) => m.status === 'running');
    if (detail.conversation.status === 'running' && running) {
      terminalRef.current = false;
      setLive({ messageId: running.id, blocks: [], streamUrl: api.aiChat.eventsUrl(id) });
    } else setLive(null);
    setError(null);
  }, []);

  const finish = useCallback(async () => {
    const current = liveRef.current;
    const id = conversation?.id;
    setLive(null);
    if (id) await load(id).catch(() => undefined);
    void invalidateLists();
    if (current) finishedRef.current?.(current.blocks);
  }, [conversation?.id, invalidateLists, load]);

  const onEvent = useCallback(
    (event: AIChatEvent) => {
      liveEventRef.current?.(event);
      if (event.type === 'error') {
        setError(event.message);
        setErrorCode(event.code);
      }
      // A (re)connected stream replays the turn from its start: the blocks start over.
      if (event.type === 'init') setLive((l) => (l ? { ...l, blocks: [] } : l));
      setLive((l) => (l ? { ...l, blocks: applyAIChatEvent(l.blocks, event) } : l));
      if (event.type === 'done' || event.type === 'error') {
        terminalRef.current = true;
        void finish();
      }
    },
    [finish],
  );
  const sse = useSse<AIChatEvent>(live?.streamUrl ?? null, {
    onEvent,
    isTerminal: (e) => e.type === 'done' || e.type === 'error',
    onError: () => undefined,
  });
  // The connection went without an end (network, proxy, the phone in the background): ask the
  // server whether the turn is over, else listen again – the replay rebuilds the answer.
  const reconnect = sse.reconnect;
  const sseStatus = sse.status;
  useEffect(() => {
    if (!live || terminalRef.current) return;
    if (sseStatus !== 'closed' && sseStatus !== 'error') return;
    const id = conversation?.id;
    if (!id) return;
    const timer = setTimeout(() => {
      void api.aiChat
        .conversation(id)
        .then((detail) => {
          if (detail.conversation.status !== 'running') {
            terminalRef.current = true;
            return finish();
          }
          reconnect();
        })
        .catch(() => reconnect());
    }, 2000);
    return () => clearTimeout(timer);
  }, [sseStatus, live, conversation?.id, finish, reconnect]);

  const reset = useCallback(() => {
    setConversation(null);
    setMessages([]);
    setLive(null);
    setError(null);
    setErrorCode(null);
  }, []);

  const send = useCallback(
    async (text: string, openRecipeId: string | null, voice = false) => {
      setError(null);
      setErrorCode(null);
      try {
        let conv = conversation;
        if (!conv) {
          conv = await api.aiChat.createConversation({});
          setConversation(conv);
          setMessages([]);
        }
        const res = await api.aiChat.sendMessage(conv.id, { text, openRecipeId, voice });
        setMessages((m) => [...m, res.userMessage]);
        terminalRef.current = false;
        setConversation((c) =>
          c ? { ...c, status: 'running', title: c.title || text.slice(0, 60) } : c,
        );
        setLive({
          messageId: res.assistantMessageId,
          blocks: [],
          streamUrl: resolveStreamUrl(res.streamUrl),
        });
        return true;
      } catch (e) {
        setError(describeError(e));
        return false;
      }
    },
    [conversation],
  );

  const compact = useCallback(async () => {
    if (!conversation) return;
    setError(null);
    try {
      const res = await api.aiChat.compact(conversation.id);
      terminalRef.current = false;
      setLive({
        messageId: res.assistantMessageId,
        blocks: [],
        streamUrl: resolveStreamUrl(res.streamUrl),
      });
    } catch (e) {
      setError(describeError(e));
    }
  }, [conversation]);

  const interrupt = useCallback(async () => {
    if (!conversation) return;
    await api.aiChat.interrupt(conversation.id).catch((e) => setError(describeError(e)));
  }, [conversation]);

  const answerAsk = useCallback(
    async (askId: string, allow: boolean) => {
      if (!conversation) return;
      await api.aiChat
        .answerAsk(conversation.id, askId, { allow })
        .catch((e) => setError(describeError(e)));
    },
    [conversation],
  );

  const rename = useCallback(
    async (title: string) => {
      if (!conversation) return;
      const updated = await api.aiChat.renameConversation(conversation.id, { title });
      setConversation(updated);
      void invalidateLists();
    },
    [conversation, invalidateLists],
  );

  const remove = useCallback(
    async (id: string) => {
      await api.aiChat.deleteConversation(id);
      if (conversation?.id === id) reset();
      void invalidateLists();
    },
    [conversation?.id, invalidateLists, reset],
  );

  return {
    conversation,
    messages,
    live,
    busy: !!live,
    error,
    errorCode,
    streamStatus: sse.status,
    load,
    reset,
    send,
    compact,
    interrupt,
    answerAsk,
    rename,
    remove,
  };
}
